export type ArticleFaqItem = Readonly<{
  question: string;
  answer: string;
}>;

/**
 * The public article architecture reserves H2 for the FAQ section and H3 for
 * every top-level question. A flat FAQPage has no parent/child question model,
 * so H4 is reserved for a future nested FAQ component only; it must never
 * replace a top-level question heading.
 */
export const ARTICLE_FAQ_HEADING_HIERARCHY = {
  section: { level: 2, tag: "h2" },
  question: { level: 3, tag: "h3" },
  nestedQuestion: { level: 4, tag: "h4", requiresParentQuestion: true },
} as const;

export const ARTICLE_FAQ_SECTION_ID = "article-faq-heading";

/**
 * Builds FAQPage data from the exact same FAQ items rendered to readers.
 * Keeping the transform here makes visible-question and schema parity explicit.
 */
export function createArticleFaqPageNode({
  slug,
  title,
  items,
}: {
  slug: string;
  title: string;
  items: readonly ArticleFaqItem[];
}) {
  if (items.length === 0) return null;

  return {
    "@type": "FAQPage",
    "@id": `https://integrated.social/blog/${slug}/#faq`,
    name: `FAQs: ${title}`,
    mainEntity: items.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}
